"use client";

import { AutomatedContentBanner, AutomatedContentTile } from "@2u-landing-pages/components/AutomatedContent";
import { ContentfulImage } from "@2u-landing-pages/components/ContentfulImage";
import { ItemsContainer } from "@2u-landing-pages/components/ItemsContainer";
import { Tile } from "@2u-landing-pages/components/Tile";
import { Banner } from "@2u-landing-pages/templates/Banner";
import {
  ContentfulImageConfig,
  isTypeContent_full,
  TypeAutomated_content_tileWithoutUnresolvableLinksResponse,
  TypeAutomated_contentWithoutUnresolvableLinksResponse,
  TypeBannerWithoutUnresolvableLinksResponse,
  TypeContent_fullWithoutUnresolvableLinksResponse,
  TypeContent_liteWithoutUnresolvableLinksResponse,
} from "@2u-landing-pages/types";
import { useParseMeta } from "@2u-landing-pages/providers";
import { renderEmbeddedEntry, renderMark, renderText } from "@2u-landing-pages/utils/contentful-richtext";
import { getCookie } from "@2u-landing-pages/utils/cookieUtils";
import EventClient from "@2u-landing-pages/utils/event-client";
import { getGradientOverlayStyle } from "@2u-landing-pages/utils/gradient-overlay-config";
import { getIconSelectionFromFields } from "@2u-landing-pages/utils/icon-selection";
import { getBorderRadius } from "@2u-landing-pages/utils/imageUtils";
import { useContentfulInspectorMode } from "@contentful/live-preview/react";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import type {
  BodyProps,
  BodyVariants,
  ButtonGroupProps,
  HeadingProps,
  HeadingVariants,
  SubheadProps,
  SubheadVariants,
} from "@red-digital/bricks";
import { Body, ButtonGroup, Heading, Subhead } from "@red-digital/bricks";
import cn from "classnames";
import React from "react";
import { Button } from "../Button";
import { hasCalendlyLink, useCalendly } from "../CalendlyWidget/useCalendly";

// @ts-ignore
import styles from "./ContentLayout.module.scss";

interface ContentLayoutProps {
  content?: TypeContent_fullWithoutUnresolvableLinksResponse | TypeContent_liteWithoutUnresolvableLinksResponse;
  className?: string;
  children?: React.ReactNode;
  topSlot?: React.ReactNode;
  eyebrow?: Omit<SubheadProps<"span">, "type" | "variant" | "children"> & {
    variant?: SubheadVariants;
  };
  heading?: Omit<HeadingProps, "variant" | "children"> & { variant?: HeadingVariants };
  subhead?: Omit<SubheadProps<"p">, "type" | "variant" | "children"> & {
    variant?: SubheadVariants;
  };
  body?: Omit<BodyProps<"p">, "variant" | "children"> & { variant?: BodyVariants };
  disclaimer?: Omit<BodyProps<"small">, "variant"> & { variant?: BodyVariants };
  image_aspect_ratio?: string;
  actions?: {
    disclaimer?: boolean;
    buttonsSlot?: React.ReactElement;
  };
  btnPlacement?: ButtonGroupProps["placement"];
  phoneNumber?: string;
}

interface CustomCSSProperties extends React.CSSProperties {
  "--eyebrow-color"?: string;
  "--heading-color"?: string;
  "--subhead-color"?: string;
  "--body-color"?: string;
  "--aspect-ratio-desktop"?: string;
  "--aspect-ratio-mobile"?: string;
  "--divider-color"?: string;
  "--text-alignment"?: string;
  "--list-alignment"?: string;
  "--button-alignment"?: string;
  "--image-alignment"?: string;
  "--rows"?: number;
}

export const ContentLayout: React.FC<ContentLayoutProps> = ({
  content,
  className,
  topSlot,
  heading,
  subhead,
  image_aspect_ratio,
  body,
  btnPlacement = "left",
  phoneNumber,
}) => {
  const inspector = useContentfulInspectorMode();
  const parseMetadata = useParseMeta();

  const isCalendlyLink = hasCalendlyLink(content?.fields?.body);
  const { calendlyClickHandler } = useCalendly(isCalendlyLink);

  if (content) {
    const fields = content?.fields || {};
    const isContentFull = isTypeContent_full(content);
    const style: CustomCSSProperties = {};

    const replaceFirstName = (text: string) => {
      const regex = /{{first_name}}/g;
      const regexWithComma = /, {{first_name}}/g;

      if (regexWithComma.test(text)) {
        const name = getCookie("taxi-first_name");
        return text.replace(regexWithComma, name ? `, ${name}` : "");
      } else if (regex.test(text)) {
        return text.replace(regex, getCookie("taxi-first_name") || "");
      }
      return text;
    };

    const imageConfig = content?.fields?.image_config as unknown as ContentfulImageConfig;
    const desktopCfg = imageConfig?.default;
    const mobileCfg = imageConfig?.mobile;

    const hasBtnLogos =
      isContentFull && content?.fields?.buttons?.some((button) => button?.fields?.logo?.fields?.file?.url);
    const hasEyebrow = isContentFull && content?.fields?.eyebrow && typeof content?.fields?.eyebrow === "object";
    const hasHeading = content?.fields?.heading && typeof content?.fields?.heading === "object";
    const hasSubhead = content?.fields?.subhead && typeof content?.fields?.subhead === "object";
    const hasBody = content?.fields?.body && typeof content?.fields?.body === "object";
    const contentCount = [hasEyebrow, hasHeading, hasSubhead, hasBody].filter(Boolean).length;
    const hasContent = Boolean(hasEyebrow || hasHeading || hasSubhead || hasBody || fields.image);
    const imagePosition = fields.image_position || "Top";
    const imagePositionMobile = fields.image_position_mobile || "Top";
    const richTextIconSize = isContentFull
      ? undefined
      : getIconSelectionFromFields(fields as Record<string, unknown>, ["size"]).size === "sm"
        ? "sm"
        : "md";

    /* Image configuration
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
    const desktopAspectRatio =
      desktopCfg?.aspectRatio && desktopCfg?.aspectRatio !== "original"
        ? desktopCfg?.aspectRatio
        : desktopCfg?.modifiedImageAspectRatio;

    const mobileAspectRatio =
      mobileCfg?.aspectRatio && mobileCfg?.aspectRatio !== "original"
        ? mobileCfg?.aspectRatio
        : mobileCfg?.modifiedImageAspectRatio;

    const isSvgImage = fields.image?.fields?.file?.contentType === "image/svg+xml";

    const desktopBorderRadius = getBorderRadius(desktopCfg?.r, isSvgImage);
    const mobileBorderRadius = getBorderRadius(mobileCfg?.r, isSvgImage);

    /* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/
    if (isContentFull && content?.fields?.eyebrow_color) {
      style["--eyebrow-color"] = `var(${content.fields.eyebrow_color})`;
    }
    if (fields.heading_color) style["--heading-color"] = `var(${content.fields.heading_color})`;
    if (fields.subhead_color) style["--subhead-color"] = `var(${content.fields.subhead_color})`;
    if (fields.body_color) style["--body-color"] = `var(${content.fields.body_color})`;
    if (fields.divider_line_color) style["--divider-color"] = `var(${content.fields.divider_line_color})`;
    if (fields.text_alignment) {
      style["--text-alignment"] = content.fields.text_alignment;
      style["--list-alignment"] = content.fields.text_alignment === "left" ? "flex-start" : "center";
    }

    if (contentCount) style["--rows"] = contentCount;

    const renderSlotContent = (entry: any, idx: number) => {
      if (!entry) return null;
      const contentType = entry?.sys?.contentType?.sys?.id;

      switch (contentType) {
        case "automated_content":
          return (
            <AutomatedContentBanner
              key={`${entry.sys.id}-${idx}`}
              {...(entry as TypeAutomated_contentWithoutUnresolvableLinksResponse).fields}
              phoneNumber={phoneNumber}
            />
          );
        case "automated_content_tile":
          return (
            <AutomatedContentTile
              key={`${entry.sys.id}-${idx}`}
              {...(entry as TypeAutomated_content_tileWithoutUnresolvableLinksResponse).fields}
              tileConfig={{
                padded: false,
              }}
              phoneNumber={phoneNumber}
            />
          );
        case "banner":
          return (
            <Banner
              key={`${entry.sys.id}-${idx}`}
              {...(entry as TypeBannerWithoutUnresolvableLinksResponse).fields}
              inspector={inspector}
            />
          );
        case "items_container":
          return <ItemsContainer key={`${entry.sys.id}-${idx}`} entry={entry} orientation="horizontal" />;
        case "tile":
          return (
            <Tile
              key={`${entry.sys.id}-${idx}`}
              content={entry}
              padded={false}
              phoneNumber={phoneNumber}
            />
          );
        default:
          return null;
      }
    };

    // Merge gradient overlay styles
    const desktopOverlayStyle = desktopCfg ? getGradientOverlayStyle(desktopCfg, "desktop") : {};
    const mobileOverlayStyle = mobileCfg ? getGradientOverlayStyle(mobileCfg, "mobile") : {};
    Object.assign(style, desktopOverlayStyle, mobileOverlayStyle);

    const handleExternalLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
      EventClient.elementClicked({
        webElement: {
          elementType: "Landing Page",
          htmlId: event.currentTarget.href,
          location: fields.name || "",
          name: event.currentTarget.innerText,
          position: "",
          text: "",
        },
      });
    };

    return (
      <>
        <div className={cn(styles.content, className)} style={style}>
          <div
            className={cn(styles.contentLayout, {
              [styles[`desktopImage${imagePosition}`]]: imagePosition,
              [styles[`mobileImage${imagePositionMobile}`]]: imagePositionMobile,
              [styles.hasButtons]: isContentFull && !!content?.fields?.buttons?.length,
              [styles.stretchImageDesktop]: desktopCfg?.stretchImage,
              [styles.stretchImageMobile]: mobileCfg?.stretchImage,
            })}
            style={
              {
                ...(desktopCfg?.stretchImage && {
                  "--image-height-desktop": `${desktopCfg?.h}px`,
                }),
                ...(mobileCfg?.stretchImage && {
                  "--image-height-mobile": `${mobileCfg?.h}px`,
                }),
                "--aspect-ratio-desktop": desktopAspectRatio,
                "--aspect-ratio-mobile": mobileAspectRatio,
                "--focal-point-desktop": desktopCfg?.focalPoint ?? "center",
                ...(desktopBorderRadius !== undefined && {
                  "--border-radius-desktop": desktopBorderRadius,
                }),
                "--focal-point-mobile": mobileCfg?.focalPoint ?? "center",
                ...(mobileBorderRadius !== undefined && {
                  "--border-radius-mobile": mobileBorderRadius,
                }),
              } as CustomCSSProperties
            }
          >
            {hasContent && (
              <div className={styles.contentContainer}>
                <div className={styles.content} style={style}>
                  {Array.isArray(fields.top_slot) && fields.top_slot.length > 0 && (
                    <div className={styles.top_slot}>
                      {fields.top_slot.map((entry, idx) => renderSlotContent(entry, idx))}
                    </div>
                  )}
                  {topSlot}
                  {fields.image && (
                    <div
                      className={cn(styles.imageContainer, {
                        [styles[`image${imagePosition}`]]: imagePosition,
                        [styles[`imageMobile${imagePositionMobile}`]]: imagePositionMobile,
                        [styles.gradientOverlay]: [desktopCfg, mobileCfg].some(
                          (cfg) => cfg?.gradientType === "gradient",
                        ),
                        [styles.solidOverlay]: [desktopCfg, mobileCfg].some((cfg) => cfg?.gradientType === "solid"),
                      })}
                    >
                      <ContentfulImage
                        image={fields.image}
                        config={fields.image_config as unknown as ContentfulImageConfig}
                        className={cn(styles.image, {
                          [styles.imageTopFull]: isContentFull && imagePosition === "Top",
                        })}
                        {...inspector({
                          entryId: content.sys.id,
                          fieldId: "image",
                        })}
                        loading="eager"
                        style={image_aspect_ratio ? { aspectRatio: image_aspect_ratio } : {}}
                      />
                    </div>
                  )}
                  {isContentFull &&
                    hasEyebrow &&
                    documentToReactComponents(content?.fields?.eyebrow!, {
                      renderMark,
                      renderText: (text) => renderText(parseMetadata ? parseMetadata(text) : text, phoneNumber),
                      renderNode: {
                        [BLOCKS.PARAGRAPH]: (node, children) => (
                          <Subhead
                            type="span"
                            className={styles.eyebrow}
                            variant="subhead-sm"
                            modifier={500}
                            {...inspector({
                              entryId: content.sys.id,
                              fieldId: "eyebrow",
                            })}
                          >
                            {children}
                          </Subhead>
                        ),
                        [INLINES.EMBEDDED_ENTRY]: (node) =>
                          renderEmbeddedEntry(
                            node,
                            "Content Layout Eyebrow",
                            content?.fields?.eyebrow,
                            phoneNumber,
                            parseMetadata,
                            richTextIconSize,
                          ),
                      },
                    })}
                  {(topSlot || hasEyebrow) && fields?.show_divider_line && <div className={styles.divider} />}
                  {hasHeading &&
                    documentToReactComponents(fields.heading!, {
                      renderNode: {
                        [BLOCKS.PARAGRAPH]: (node, children) => (
                          <Heading
                            variant="heading-2xl"
                            modifier={700}
                            {...heading}
                            className={cn(styles.heading, heading?.className)}
                            {...inspector({
                              entryId: content.sys.id,
                              fieldId: "heading",
                            })}
                          >
                            {children}
                          </Heading>
                        ),
                        [INLINES.EMBEDDED_ENTRY]: (node) =>
                          renderEmbeddedEntry(
                            node,
                            "Content Layout Heading",
                            fields.heading,
                            phoneNumber,
                            parseMetadata,
                            richTextIconSize,
                          ),
                      },
                      renderText: (text) =>
                        renderText(
                          parseMetadata ? parseMetadata(replaceFirstName(text)) : replaceFirstName(text),
                          phoneNumber,
                        ),
                    })}
                  {hasSubhead &&
                    documentToReactComponents(fields.subhead!, {
                      renderMark,
                      renderText: (text) => renderText(parseMetadata ? parseMetadata(text) : text, phoneNumber),
                      renderNode: {
                        [BLOCKS.PARAGRAPH]: (node, children) => (
                          <Subhead
                            type="p"
                            variant="subhead-md"
                            modifier={500}
                            {...subhead}
                            className={cn(styles.subhead, subhead?.className)}
                            {...inspector({
                              entryId: content.sys.id,
                              fieldId: "subhead",
                            })}
                          >
                            {children}
                          </Subhead>
                        ),
                        [INLINES.EMBEDDED_ENTRY]: (node) =>
                          renderEmbeddedEntry(
                            node,
                            "Content Layout Subhead",
                            fields.subhead,
                            phoneNumber,
                            parseMetadata,
                            richTextIconSize,
                          ),
                      },
                    })}
                  {hasBody &&
                    documentToReactComponents(fields.body!, {
                      renderMark,
                      renderText: (text) => renderText(parseMetadata ? parseMetadata(text) : text, phoneNumber),
                      renderNode: {
                        [BLOCKS.PARAGRAPH]: (node, children) => (
                          <Body
                            {...inspector({
                              entryId: content.sys.id,
                              fieldId: "body",
                            })}
                            className={styles.body}
                            variant="body-md"
                            {...body}
                          >
                            {children}
                          </Body>
                        ),
                        [INLINES.EMBEDDED_ENTRY]: (node) =>
                          renderEmbeddedEntry(
                            node,
                            "Content Layout Body",
                            fields.body,
                            phoneNumber,
                            parseMetadata,
                            richTextIconSize,
                          ),
                        [INLINES.HYPERLINK]: (node: any) => {
                          const { uri } = node?.data;
                          const content = node?.content?.[0];
                          if (content && "value" in content) {
                            if (isCalendlyLink) {
                              return (
                                <a href="#" onClick={calendlyClickHandler(uri)} className={styles.calendlyLink}>
                                  {content.value}
                                </a>
                              );
                            }
                            return (
                              <a href={uri} target="_blank" rel="noopener noreferrer" onClick={handleExternalLinkClick}>
                                {content.value}
                              </a>
                            );
                          }
                          return null;
                        },
                      },
                    })}
                  {Array.isArray(fields.bottom_slot) && fields.bottom_slot.length > 0 && (
                    <div className={styles.bottom_slot}>
                      {fields.bottom_slot.map((entry, idx) => renderSlotContent(entry, idx))}
                    </div>
                  )}
                </div>
              </div>
            )}
            {isContentFull && content?.fields?.buttons && (
              <ButtonGroup
                className={cn(styles.actions, styles.buttonsContainer, { [styles.btnLogos]: hasBtnLogos })}
                layout="inline"
                placement={btnPlacement}
              >
                {content.fields.buttons.map((button, index) => (
                  <Button
                    key={`${button?.sys?.id}-${index}`}
                    {...button?.fields}
                    phoneNumber={phoneNumber}
                    location={content?.fields?.name || ""}
                  >
                    {button?.fields?.label}
                  </Button>
                ))}
              </ButtonGroup>
            )}
          </div>
        </div>
      </>
    );
  }

  return null;
};
